import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { UploadButton } from "../components/AuthImg";
import { toast } from "sonner";
import { ArrowLeft, Plus, Check, Trash2, Search, ListTodo, RotateCcw, Users, ImageIcon, X, MessageSquare, CircleSlash, Edit2 } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";
import { TaskAttachmentList, TaskAttachmentPicker } from "../components/TaskAttachments";

export default function Tasks() {
  const confirm = useConfirm();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [persons, setPersons] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending | done
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // task being edited
  const [completing, setCompleting] = useState(null); // task being completed via dialog

  const load = async () => {
    try {
      const [t, p] = await Promise.all([
        api.get("/tasks"),
        isAdmin ? api.get("/persons").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setTasks(t.data || []);
      setPersons((p.data || []).filter((x) => x.active !== false));
    } catch { toast.error("Eroare la încărcare"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const visible = useMemo(() => {
    let list = tasks;
    if (filter === "pending") list = tasks.filter((t) => t.status === "pending");
    if (filter === "done") list = tasks.filter((t) => t.status === "done" || t.status === "partial");
    const prioRank = { urgent: 0, high: 1, normal: 2, low: 3 };
    // Sortare: pending urgent → pending high → pending normal/low → completed (păstrând ordinea cronologică între task-uri completate)
    return [...list].sort((a, b) => {
      const aDone = a.status === "done" || a.status === "partial";
      const bDone = b.status === "done" || b.status === "partial";
      if (aDone !== bDone) return aDone ? 1 : -1;
      // Pentru pending: prioritate desc; pentru completed: dată completare desc
      if (!aDone) {
        const ra = prioRank[(a.priority || "normal").toLowerCase()] ?? 9;
        const rb = prioRank[(b.priority || "normal").toLowerCase()] ?? 9;
        if (ra !== rb) return ra - rb;
      }
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [tasks, filter]);

  const counts = useMemo(() => ({
    all: tasks.length,
    pending: tasks.filter((t) => t.status === "pending").length,
    done: tasks.filter((t) => t.status === "done" || t.status === "partial").length,
  }), [tasks]);

  const openComplete = (t) => setCompleting(t);

  const uncheckTask = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}/uncheck`);
      toast.success("Task debifat — revenit la activ");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const deleteTask = async (t) => {
    if (!(await confirm({ body: `Șterge task-ul "${t.title}"?`, danger: true }))) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task șters");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <div data-testid="page-tasks">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 text-sm">
            <ArrowLeft size={14}/> Înapoi
          </button>
          <div>
            <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
              <ListTodo className="text-[var(--primary)]"/> Task-uri
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              {isAdmin ? "Atribuie sarcini angajaților" : "Sarcinile tale active"}
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreating(true)} className="bg-[var(--primary)]" data-testid="add-task-btn">
            <Plus size={16}/> Task nou
          </Button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {[
          { v: "all", label: `Toate (${counts.all})` },
          { v: "pending", label: `În așteptare (${counts.pending})` },
          { v: "done", label: `Efectuate (${counts.done})` },
        ].map((b) => (
          <button
            key={b.v}
            onClick={() => setFilter(b.v)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filter === b.v ? "bg-[var(--primary)] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            data-testid={`filter-${b.v}`}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {visible.length === 0 ? (
          <div className="card-flat p-8 text-center text-zinc-500 italic">
            {filter === "done" ? "Niciun task efectuat încă." : filter === "pending" ? "Niciun task activ." : "Niciun task."}
          </div>
        ) : (
          visible.map((t) => {
            const role = t.created_by_role || "admin";
            const roleConfig = {
              admin:      { icon: "👑", label: "Admin",      ringColor: "border-l-blue-500",   bgColor: "bg-blue-50/40" },
              magazioner: { icon: "🔧", label: "Magazioner", ringColor: "border-l-amber-500",  bgColor: "bg-amber-50/40" },
              planificator: { icon: "📋", label: "Planificator", ringColor: "border-l-violet-500", bgColor: "bg-violet-50/40" },
              user:       { icon: "👷", label: "Angajat",    ringColor: "border-l-emerald-500", bgColor: "bg-emerald-50/40" },
            }[role] || { icon: "•", label: role, ringColor: "border-l-zinc-400", bgColor: "" };
            const isDone = t.status === "done";
            const isPartial = t.status === "partial";
            const isCompleted = isDone || isPartial;
            const prio = (t.priority || "normal").toLowerCase();
            const isUrgent = prio === "urgent";
            const isHigh = prio === "high";
            // assignee identification (matches backend)
            const isMyTask = (user?.person_id && (t.assigned_person_ids || []).includes(user.person_id))
              || (user?.id && (t.assigned_user_ids || []).includes(user.id));
            const canToggle = isAdmin || isMyTask;
            const cardExtraCls = !isCompleted && isUrgent
              ? "ring-2 ring-red-500 ring-offset-1 bg-red-50/60 animate-pulse-slow"
              : !isCompleted && isHigh
                ? "ring-2 ring-red-300 bg-red-50/40"
                : "";
            return (
            <div
              key={t.id}
              className={`card-flat p-4 flex items-start gap-3 border-l-4 ${roleConfig.ringColor} ${isCompleted ? "opacity-80" : roleConfig.bgColor} ${cardExtraCls}`}
              data-testid={`task-${t.id}`}
            >
              <button
                onClick={() => {
                  if (!canToggle) return;
                  if (isCompleted) {
                    // Debifare — revine la pending
                    uncheckTask(t);
                  } else {
                    openComplete(t);
                  }
                }}
                className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isDone
                    ? "bg-green-500 border-green-500 text-white"
                    : isPartial
                      ? "bg-amber-400 border-amber-500 text-white"
                      : "border-zinc-300 hover:border-green-500"
                }`}
                data-testid={isCompleted ? `uncheck-${t.id}` : `complete-${t.id}`}
                disabled={!canToggle}
                title={isCompleted ? "Click ca să debifezi" : "Marchează efectuat / parțial"}
              >
                {isDone && <Check size={14}/>}
                {isPartial && <span className="text-[10px] font-bold leading-none">½</span>}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold flex items-center gap-2 flex-wrap ${isDone ? "line-through text-zinc-500" : ""}`}>
                  {isUrgent && !isCompleted && (
                    <span
                      className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse shadow-sm"
                      data-testid={`task-priority-badge-${t.id}`}
                      title="Task urgent — necesită atenție imediată"
                    >
                      ⚡ URGENT
                    </span>
                  )}
                  {isHigh && !isCompleted && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300"
                      data-testid={`task-priority-badge-${t.id}`}
                    >
                      🔴 Prioritate mare
                    </span>
                  )}
                  <span>{t.title}</span>
                  {isPartial && (
                    <span className="ml-2 inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                      🟡 parțial
                    </span>
                  )}
                </div>
                {t.description && <div className="text-sm text-zinc-600 mt-1 whitespace-pre-wrap">{t.description}</div>}
                {/* Atașamente la creare (poze/video de la creator) */}
                {(t.attachments || []).length > 0 && (
                  <div className="mt-2" data-testid={`task-creation-attachments-${t.id}`}>
                    <TaskAttachmentList attachments={t.attachments} testPrefix={`task-catt-${t.id}`} />
                  </div>
                )}
                {/* Notă finalizare + atașamente */}
                {isCompleted && (t.completion_note || (t.completion_attachments || []).length > 0) && (
                  <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2 space-y-2" data-testid={`task-completion-${t.id}`}>
                    {t.completion_note && (
                      <div className="flex items-start gap-1.5 text-[12px] text-zinc-700">
                        <MessageSquare size={12} className="mt-0.5 text-zinc-400 shrink-0"/>
                        <span className="whitespace-pre-wrap break-words">{t.completion_note}</span>
                      </div>
                    )}
                    {(t.completion_attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {t.completion_attachments.map((a) => {
                          const isImg = (a.content_type || "").startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(a.name || "");
                          return (
                            <a
                              key={a.file_id}
                              href={fileUrl(a.file_id)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] bg-white border border-zinc-200 rounded p-1 hover:border-blue-400"
                              data-testid={`task-attachment-${t.id}-${a.file_id}`}
                            >
                              {isImg ? (
                                <img src={fileUrl(a.file_id)} alt={a.name} className="w-10 h-10 object-cover rounded"/>
                              ) : (
                                <span className="w-10 h-10 grid place-items-center bg-zinc-100 rounded text-zinc-500">📎</span>
                              )}
                              <span className="text-zinc-700 max-w-[120px] truncate">{a.name}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-zinc-500">
                  <span className="inline-flex items-center gap-1">
                    <Users size={11}/> {(t.assigned_names || []).join(", ") || "—"}
                  </span>
                  {t.due_date && <span>📅 până la {fmtRoDate(t.due_date)}</span>}
                  <span title={`Creator: ${roleConfig.label}`} data-testid={`task-creator-${t.id}`}>
                    <span className="mr-1">{roleConfig.icon}</span>De la {t.created_by_name} · {fmtRoDate(t.created_at)}
                  </span>
                  {isCompleted && t.completed_by_name && (
                    <span className={isPartial ? "text-amber-700" : "text-green-700"}>
                      {isPartial ? "½" : "✓"} {t.completed_by_name} · {fmtRoDate(t.completed_at)}
                    </span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => setEditing(t)}
                    className="text-blue-600 hover:bg-blue-50 rounded p-1.5"
                    data-testid={`edit-${t.id}`}
                    title="Editează (modifică titlu/descriere/persoane)"
                  >
                    <Edit2 size={14}/>
                  </button>
                  <button
                    onClick={() => deleteTask(t)}
                    className="text-red-500 hover:bg-red-50 rounded p-1.5"
                    data-testid={`delete-${t.id}`}
                    title="Șterge"
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              )}
            </div>
            );
          })
        )}
      </div>

      {creating && isAdmin && (
        <CreateTaskDialog
          persons={persons}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}

      {editing && isAdmin && (
        <CreateTaskDialog
          persons={persons}
          task={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {completing && (
        <CompleteTaskDialog
          task={completing}
          onClose={() => setCompleting(null)}
          onSaved={() => { setCompleting(null); load(); }}
        />
      )}
    </div>
  );
}

export function CompleteTaskDialog({ task, onClose, onSaved }) {
  const [partial, setPartial] = useState(false);
  const [note, setNote] = useState("");
  const [attachments, setAttachments] = useState([]); // [{file_id, name, content_type}]
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}/complete`, {
        partial,
        note: note.trim() || null,
        attachments,
      });
      toast.success(partial ? "Task marcat ca parțial efectuat" : "Task marcat ca efectuat");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="task-complete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {partial ? <span className="text-amber-600">🟡 Marchează parțial efectuat</span> : <span className="text-emerald-700">✓ Marchează efectuat</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Task</div>
            <div className="font-semibold text-zinc-900">{task.title}</div>
            {task.description && <div className="text-xs text-zinc-600 mt-1 whitespace-pre-wrap line-clamp-3">{task.description}</div>}
          </div>

          {/* Toggle parțial */}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="task-partial-toggle">
            <Checkbox checked={partial} onCheckedChange={(v) => setPartial(!!v)} />
            <span className="text-sm">
              <strong>Doar parțial efectuat</strong> <span className="text-zinc-500">— task-ul mai are de continuat</span>
            </span>
          </label>

          {/* Notă */}
          <div>
            <Label className="small-label flex items-center gap-1"><MessageSquare size={11}/> Notă (opțional)</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ex: am terminat 60%, mai rămân ușile de pe etajul 2"
              className="mt-1 text-sm"
              data-testid="task-note"
            />
          </div>

          {/* Poze / Video */}
          <div>
            <Label className="small-label flex items-center gap-1"><ImageIcon size={11}/> Poze / Video (opțional)</Label>
            <div className="mt-1">
              <TaskAttachmentPicker attachments={attachments} setAttachments={setAttachments} testId="task-upload" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Poți încărca mai multe fișiere (imagini, video sau PDF).</div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="task-complete-cancel">
            Anulează
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className={partial ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
            data-testid="task-complete-save"
          >
            {saving ? "Se salvează..." : partial ? "🟡 Salvează ca parțial" : "✓ Marchează efectuat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateTaskDialog({ persons, task, onClose, onSaved }) {
  const isEdit = !!task;
  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [dueDate, setDueDate] = useState(task?.due_date || "");
  const [priority, setPriority] = useState(task?.priority || "normal");
  const [selected, setSelected] = useState(task?.assigned_person_ids || []);
  const [attachments, setAttachments] = useState(task?.attachments || []);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredPersons = persons.filter((p) =>
    !search || `${p.nume || ""} ${p.prenume || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => {
    if (selected.length === filteredPersons.length) setSelected([]);
    else setSelected(filteredPersons.map((p) => p.id));
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Titlul este obligatoriu"); return; }
    if (description.trim().length < 10) { toast.error("Descrierea este obligatorie — minim 10 caractere"); return; }
    if (selected.length === 0) { toast.error("Selectează cel puțin un angajat"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        due_date: dueDate || null,
        priority: priority || "normal",
        assigned_person_ids: selected,
        attachments,
        // păstrăm și user_ids existente (dacă există) ca să nu le pierdem la edit
        assigned_user_ids: task?.assigned_user_ids || [],
      };
      if (isEdit) {
        await api.put(`/tasks/${task.id}`, payload);
        toast.success("Task actualizat");
      } else {
        await api.post("/tasks", payload);
        toast.success(`Task creat pentru ${selected.length} ${selected.length === 1 ? "angajat" : "angajați"}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const PRIORITY_OPTIONS = [
    { v: "low",    label: "🟢 Scăzut",      cls: "border-zinc-300 text-zinc-700 data-[on=true]:bg-zinc-100" },
    { v: "normal", label: "🟡 Normal",      cls: "border-amber-300 text-amber-800 data-[on=true]:bg-amber-100" },
    { v: "high",   label: "🔴 Mare",        cls: "border-red-300 text-red-800 data-[on=true]:bg-red-100" },
    { v: "urgent", label: "⚡ URGENT",      cls: "border-red-600 text-red-700 data-[on=true]:bg-red-600 data-[on=true]:text-white" },
  ];

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="text-[var(--primary)]"/> {isEdit ? "Editează task" : "Task nou"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label className="small-label">Titlu</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex: Pregătire materiale pentru luni"
              data-testid="task-title"
              autoFocus
            />
          </div>
          <div>
            <Label className="small-label">Detalii (obligatoriu, minim 10 caractere)</Label>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrie clar ce trebuie făcut (minim 10 caractere)…"
              data-testid="task-description"
            />
            {description.trim().length > 0 && description.trim().length < 10 && (
              <div className="text-[11px] text-red-600 mt-0.5">Încă {10 - description.trim().length} caractere necesare</div>
            )}
          </div>
          <div>
            <Label className="small-label">Termen (opțional)</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="task-due"/>
          </div>
          <div>
            <Label className="small-label flex items-center gap-1"><ImageIcon size={11}/> Fotografii / Filmări (opțional)</Label>
            <div className="mt-1">
              <TaskAttachmentPicker attachments={attachments} setAttachments={setAttachments} testId="task-create-upload" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Poze sau video care explică task-ul — vizibile destinatarilor.</div>
          </div>
          <div>
            <Label className="small-label">Prioritate</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="task-priority-group">
              {PRIORITY_OPTIONS.map((opt) => {
                const isOn = priority === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setPriority(opt.v)}
                    data-on={isOn}
                    data-testid={`task-priority-${opt.v}`}
                    className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border-2 transition-all ${opt.cls} ${isOn ? "ring-2 ring-offset-1 scale-[1.03]" : "bg-white hover:scale-[1.02]"} ${opt.v === "urgent" && isOn ? "animate-pulse" : ""}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {priority === "urgent" && (
              <p className="text-[11px] text-red-700 mt-1.5 font-medium" data-testid="task-priority-urgent-note">
                ⚡ Acest task va apărea ca <strong>URGENT</strong> destinatarilor. Vei primi notificare când e executat.
              </p>
            )}
          </div>
          <div className="border-t border-[var(--border)] pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="small-label">Atribuie către ({selected.length} selectați)</Label>
              <button onClick={toggleAll} className="text-xs text-[var(--primary)] hover:underline" type="button">
                {selected.length === filteredPersons.length && filteredPersons.length > 0 ? "Deselectează tot" : "Selectează tot"}
              </button>
            </div>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
              <Input className="pl-8" placeholder="Caută…" value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <div className="max-h-[260px] overflow-y-auto border border-[var(--border)] rounded-md">
              {filteredPersons.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-zinc-50 cursor-pointer border-b border-zinc-100 last:border-b-0"
                  data-testid={`task-person-${p.id}`}
                >
                  <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="text-sm font-medium">{p.nume} {p.prenume}</span>
                </label>
              ))}
              {filteredPersons.length === 0 && <div className="text-xs text-zinc-400 italic p-3 text-center">Niciun rezultat.</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="submit-task">
            {saving ? "Se salvează…" : (isEdit ? "Salvează modificările" : "Creează task")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
